import React, { useState, useContext, useEffect } from 'react';
import {
  Typography, Upload, Button, Alert, Descriptions, Tag, Space, Spin,
  message, Table, Segmented, DatePicker, Input,
} from 'antd';
import {
  InboxOutlined, UploadOutlined, SaveOutlined,
  CloudUploadOutlined, TableOutlined, IdcardOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';
import { UserContext, hasGrant } from '../App';

const { Dragger } = Upload;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const hrs = (secs) => (secs == null ? '—' : `${(secs / 3600).toFixed(2)}h`);
const hhmm = (secs) => {
  if (secs == null) return '—';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return `${h}:${String(m).padStart(2, '0')}`;
};
// '2026-09-24 11:31:00' -> '11:31'. The date is already the row's own column.
const clock = (v) => (v ? String(v).slice(11, 16) : '—');

export default function CNELoginUploadPage() {
  const currentUser = useContext(UserContext);
  // Role, or an explicit grant for this page. The grant is additive, so it
  // opens the page for someone outside the CNE team and never closes it.
  const isAuthorized = currentUser?.Role === 'admin' || currentUser?.Role === 'qa_admin'
    || (currentUser?.JobID === 6 && currentUser?.TeamID === 3)
    || hasGrant(currentUser, 'cne-login-upload');

  const [view, setView] = useState('upload');

  const [file, setFile]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError]     = useState(null);
  const [saving, setSaving]   = useState(false);
  const [result, setResult]   = useState(null);

  const [range, setRange]         = useState([dayjs().subtract(1, 'month'), dayjs()]);
  const [uploaded, setUploaded]   = useState([]);
  const [upLoading, setUpLoading] = useState(false);

  // The AWS ID mapping tab. This table stores no UserID -- every read joins on
  // AWSID -- so mapping an agent here links every session they have ever
  // appeared on, and their daily stats with it, since both key on the same id.
  const [teamUsers, setTeamUsers]         = useState([]);
  const [unmapped, setUnmapped]           = useState([]);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [awsIdDraft, setAwsIdDraft]       = useState({});
  const [savingIds, setSavingIds]         = useState(new Set());

  useEffect(() => {
    if (!isAuthorized || view !== 'browse' || !range?.[0] || !range?.[1]) return;
    setUpLoading(true);
    api.get('/api/admin/cne-login/uploaded', {
      params: {
        date_from: range[0].format('YYYY-MM-DD'),
        date_to:   range[1].format('YYYY-MM-DD'),
      },
    })
      .then((r) => setUploaded(r.data || []))
      .catch(() => setUploaded([]))
      .finally(() => setUpLoading(false));
  }, [isAuthorized, view, range]);

  useEffect(() => {
    if (!isAuthorized || view !== 'aws-ids') return;
    setMappingLoading(true);
    Promise.all([
      api.get('/api/admin/cne-team-users'),
      api.get('/api/admin/cne-login/unmapped').catch(() => ({ data: [] })),
    ])
      .then(([usersRes, unmappedRes]) => {
        setTeamUsers(usersRes.data || []);
        setUnmapped(unmappedRes.data || []);
      })
      .catch(() => { setTeamUsers([]); setUnmapped([]); })
      .finally(() => setMappingLoading(false));
  }, [isAuthorized, view]);

  const reset = () => { setPreview(null); setError(null); setResult(null); };

  const handlePreview = async () => {
    if (!file) { message.warning('Select a file first.'); return; }
    setLoading(true); reset();
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await api.post('/api/admin/cne-login/preview', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not read the file.');
    } finally {
      setLoading(false);
    }
  };

  const handleCommit = async () => {
    setSaving(true);
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await api.post('/api/admin/cne-login/commit', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      setPreview(null);
      setFile(null);
      message.success('Upload saved.');
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const saveAwsId = async (userId) => {
    setSavingIds((s) => new Set(s).add(userId));
    try {
      await api.put('/api/admin/cne-aws-id', { user_id: userId, aws_id: awsIdDraft[userId] || null });
      setTeamUsers((prev) => prev.map((u) =>
        u.ID === userId ? { ...u, AWSID: awsIdDraft[userId] || null } : u));
      setUnmapped((prev) => prev.filter((r) => r.AWSID !== awsIdDraft[userId]));
      message.success('AWS ID saved.');
    } catch {
      message.error('Could not save the AWS ID.');
    } finally {
      setSavingIds((s) => { const n = new Set(s); n.delete(userId); return n; });
    }
  };

  if (!isAuthorized) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" showIcon message="You do not have access to this page." />
      </div>
    );
  }

  const agentCols = [
    {
      title: 'Agent', key: 'agent', width: 280,
      render: (_, a) => (
        <div>
          <div>{a.DisplayName || a.AgentName || <Text type="secondary">Unknown</Text>}</div>
          {/* The id is shown even when matched: it is what the row stores, and
              a wrong match is only visible next to it. */}
          <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>{a.AWSID}</Text>
          {!a.UserID && <Tag color="orange" style={{ marginLeft: 6 }}>No AWS ID on file</Tag>}
          {a.IsActive === false && <Tag color="red" style={{ marginLeft: 6 }}>Inactive</Tag>}
        </div>
      ),
    },
    { title: 'Sessions', dataIndex: 'Sessions', key: 'Sessions', width: 90, align: 'right',
      sorter: (a, b) => a.Sessions - b.Sessions },
    { title: 'Open', dataIndex: 'OpenSessions', key: 'OpenSessions', width: 80, align: 'right',
      sorter: (a, b) => a.OpenSessions - b.OpenSessions,
      render: (v) => (v > 0 ? <Tag color="blue">{v}</Tag> : <Text type="secondary">0</Text>) },
    { title: 'Logged In', dataIndex: 'TotalSecs', key: 'TotalSecs', width: 110, align: 'right',
      sorter: (a, b) => a.TotalSecs - b.TotalSecs, render: hrs },
    { title: 'First Day', dataIndex: 'FirstDate', key: 'FirstDate', width: 110 },
    { title: 'Last Day',  dataIndex: 'LastDate',  key: 'LastDate',  width: 110 },
  ];

  const uploadedCols = [
    { title: 'Date', dataIndex: 'LoginDate', key: 'LoginDate', width: 110,
      defaultSortOrder: 'descend',
      sorter: (a, b) => (a.LoginDate || '').localeCompare(b.LoginDate || '') },
    { title: 'Agent', dataIndex: 'DisplayName', key: 'DisplayName', width: 200, ellipsis: true,
      sorter: (a, b) => (a.DisplayName || '').localeCompare(b.DisplayName || '') },
    { title: 'Login',  key: 'in',  width: 90, align: 'right', render: (_, r) => clock(r.LoginAt) },
    { title: 'Logout', key: 'out', width: 90, align: 'right',
      render: (_, r) => (r.LogoutAt ? clock(r.LogoutAt) : <Tag color="blue">open</Tag>) },
    { title: 'Duration', dataIndex: 'DurationSecs', key: 'DurationSecs', width: 100, align: 'right',
      sorter: (a, b) => (a.DurationSecs ?? -1) - (b.DurationSecs ?? -1), render: hhmm },
    { title: 'Record', dataIndex: 'RecordName', key: 'RecordName', width: 150,
      render: (v) => <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>{v || '—'}</Text> },
  ];

  const mappingCols = [
    { title: 'Name', dataIndex: 'DisplayName', key: 'name', width: 240,
      sorter: (a, b) => (a.DisplayName || '').localeCompare(b.DisplayName || '') },
    {
      title: 'AWS Agent ID', key: 'awsid',
      render: (_, u) => (
        <Input
          size="small"
          placeholder="username@userId@orgId"
          value={awsIdDraft[u.ID] ?? u.AWSID ?? ''}
          onChange={(e) => setAwsIdDraft((s) => ({ ...s, [u.ID]: e.target.value }))}
        />
      ),
    },
    {
      title: '', key: 'save', width: 80,
      render: (_, u) => (
        <Button
          size="small" icon={<SaveOutlined />}
          loading={savingIds.has(u.ID)}
          disabled={(awsIdDraft[u.ID] ?? u.AWSID ?? '') === (u.AWSID ?? '')}
          onClick={() => saveAwsId(u.ID)}
        >
          Save
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24, maxWidth: view === 'browse' ? '100%' : 1100 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 16, gap: 16, flexWrap: 'wrap',
      }}>
        <Title level={4} style={{ margin: 0 }}>CNE Login / Logout</Title>
        <Segmented
          value={view}
          onChange={(v) => { setView(v); setError(null); }}
          options={[
            { label: 'Import',               value: 'upload',  icon: <CloudUploadOutlined /> },
            { label: 'View Uploaded',        value: 'browse',  icon: <TableOutlined /> },
            { label: 'Update AWS Agent IDs', value: 'aws-ids', icon: <IdcardOutlined /> },
          ]}
        />
      </div>

      {view === 'upload' && (
        <>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Upload the User Login Status export (one row per session). Logged-in time is time
            on the CCaaS, which is team based, so there is nothing to choose per agent: these
            are the CNE team's sessions. Re-uploading replaces every session on the dates the
            file covers, so a corrected file corrects the day rather than doubling it.
          </Text>

          {!preview && (
            <>
              <Dragger
                accept=".tsv,.txt,.csv,.xls"
                beforeUpload={(f) => { setFile(f); reset(); return false; }}
                fileList={file ? [file] : []}
                onRemove={() => { setFile(null); reset(); }}
                maxCount={1}
                style={{ marginBottom: 16 }}
              >
                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                <p className="ant-upload-text">Click or drag the export here</p>
                <p className="ant-upload-hint">Tab-separated User Login Status export (.txt or .tsv)</p>
              </Dragger>
              <Button type="primary" icon={<UploadOutlined />} loading={loading}
                      onClick={handlePreview} disabled={!file}>
                Read File
              </Button>
            </>
          )}

          {error && <Alert type="error" message={error} showIcon style={{ marginTop: 16 }} />}

          {result && (
            <Alert
              type="success" showIcon style={{ marginTop: 16 }}
              message="Upload saved"
              description={
                <Descriptions size="small" column={3} style={{ marginTop: 8 }}>
                  <Descriptions.Item label="Sessions"><Tag color="green">{result.saved}</Tag></Descriptions.Item>
                  <Descriptions.Item label="Agents">{result.agents}</Descriptions.Item>
                  <Descriptions.Item label="Dates">{result.date_from} to {result.date_to}</Descriptions.Item>
                  <Descriptions.Item label="No AWS ID on file">
                    {result.unmatched?.length > 0
                      ? <Tag color="orange">{result.unmatched.length}</Tag>
                      : 0}
                  </Descriptions.Item>
                </Descriptions>
              }
            />
          )}

          {preview && (
            <>
              <Descriptions size="small" column={4} bordered style={{ marginBottom: 16 }}>
                <Descriptions.Item label="Sessions">{preview.row_count}</Descriptions.Item>
                <Descriptions.Item label="Agents">{preview.agents?.length ?? 0}</Descriptions.Item>
                <Descriptions.Item label="Days">{preview.day_count}</Descriptions.Item>
                <Descriptions.Item label="Dates">{preview.date_from} to {preview.date_to}</Descriptions.Item>
              </Descriptions>

              {preview.unmatched?.length > 0 && (
                <Alert
                  type="warning" showIcon style={{ marginBottom: 16 }}
                  message={`${preview.unmatched.length} agent(s) have no AWS ID on a QAUser.`}
                  description={
                    'Their sessions are still saved. Fill the id in under Update AWS Agent IDs '
                    + 'and every session it has ever appeared on links, with nothing to re-upload.'
                  }
                />
              )}

              {/* Silent when the export behaves, which it does today: every row's
                  Created Date is the day its login falls on. Shown only if that
                  ever stops being true, since the day a session is filed under
                  comes from the login timestamp. */}
              {preview.day_mismatch > 0 && (
                <Alert
                  type="warning" showIcon style={{ marginBottom: 16 }}
                  message={`${preview.day_mismatch} session(s) have a Created Date that is not the login day.`}
                  description={
                    'Sessions are filed under the day they started. Worth a look at those rows '
                    + 'before saving -- the export has always agreed with that until now.'
                  }
                />
              )}

              {preview.open_count > 0 && (
                <Alert
                  type="info" showIcon style={{ marginBottom: 16 }}
                  message={`${preview.open_count} session(s) have no logout time.`}
                  description="Saved with no logout and no duration, which is what an open session is."
                />
              )}

              <Table
                rowKey="AWSID"
                size="small"
                dataSource={preview.agents || []}
                columns={agentCols}
                pagination={false}
                scroll={{ y: 420 }}
                style={{ marginBottom: 16 }}
              />

              <Space>
                <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleCommit}>
                  Save {preview.row_count} session{preview.row_count === 1 ? '' : 's'}
                </Button>
                <Button onClick={() => { setPreview(null); setFile(null); reset(); }}>Cancel</Button>
              </Space>
            </>
          )}
        </>
      )}

      {view === 'browse' && (
        <>
          <Space style={{ marginBottom: 16 }} wrap>
            <RangePicker value={range} onChange={(v) => v && setRange(v)} allowClear={false} />
            {uploaded.length > 0 && (
              <Text type="secondary">{uploaded.length} session{uploaded.length === 1 ? '' : 's'}</Text>
            )}
          </Space>
          <Table
            rowKey={(r) => r.RecordName || `${r.AWSID}-${r.LoginAt}`}
            size="small"
            dataSource={uploaded}
            columns={uploadedCols}
            loading={upLoading}
            scroll={{ x: 'max-content' }}
            pagination={{ defaultPageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} sessions` }}
            locale={{ emptyText: 'Nothing uploaded for this range.' }}
          />
        </>
      )}

      {view === 'aws-ids' && (
        <Spin spinning={mappingLoading}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            The same AWS ID the daily stats upload uses. One id per person covers both, so
            filling one in here links their sessions and their daily stats at once.
          </Text>

          {unmapped.length > 0 && (
            <Alert
              type="warning" showIcon style={{ marginBottom: 16 }}
              message={`${unmapped.length} AWS ID(s) in stored sessions match no QAUser`}
              description={
                <Table
                  rowKey="AWSID"
                  size="small"
                  pagination={false}
                  style={{ marginTop: 8 }}
                  dataSource={unmapped}
                  columns={[
                    { title: 'Name on the export', dataIndex: 'AgentName', key: 'AgentName', width: 200,
                      render: (v) => v || <Text type="secondary">—</Text> },
                    { title: 'AWS ID', dataIndex: 'AWSID', key: 'AWSID', ellipsis: true,
                      render: (v) => <Text style={{ fontSize: 11, fontFamily: 'monospace' }} copyable>{v}</Text> },
                    { title: 'Sessions', dataIndex: 'Sessions', key: 'Sessions', width: 90, align: 'right' },
                    { title: 'First', dataIndex: 'FirstDate', key: 'FirstDate', width: 105 },
                    { title: 'Last',  dataIndex: 'LastDate',  key: 'LastDate',  width: 105 },
                  ]}
                />
              }
            />
          )}

          <Table
            rowKey="ID"
            size="small"
            dataSource={teamUsers}
            columns={mappingCols}
            pagination={{ defaultPageSize: 25, showSizeChanger: true }}
          />
        </Spin>
      )}
    </div>
  );
}
